import { Component, OnInit, OnDestroy, HostListener, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Location } from '@angular/common';
import { skip, takeUntil } from 'rxjs/operators';
import { Metadata, RunView } from '@memberjunction/core';
import { BaseResourceComponent } from '@memberjunction/ng-shared';
import { RegisterClass, UUIDsEqual } from '@memberjunction/global';
import { ResourceData, MJUserSettingEntity } from '@memberjunction/core-entities';
import { mjBizAppsCommonOrganizationEntity } from '@mj-biz-apps/common-entities';
import { IzzyMetadataEngine } from 'mj_generatedentities';
import { BCSaaSChannelEntity } from '@bluecypress/bcsaas-entities';
import { IzzyAuthService } from '../../services/izzy-auth.service';
import { IzzyContextService } from '../../services/izzy-context.service';
import { OrgSelectorComponent } from '../org-selector/org-selector.component';

/**
 * Section definition for left nav
 */
interface MessagesSection {
  id: string;
  label: string;
  icon: string;
  description: string;
}

/**
 * User settings structure for persistence
 */
interface MessagesContainerSettings {
  isNavCollapsed: boolean;
  organizationId?: string;
  channelId?: string;
  selectedSection?: string;
  channelOrder?: Record<string, string[]>; // orgId -> ordered channelIds
}

/**
 * Messages Container Component
 *
 * Top-level container with left navigation for message-related panels:
 * - Review & Approve: Approve/reject workflow
 * - Search: Cross-org message search with timeline view
 */
@Component({
  standalone: false,
  selector: 'izzy-messages-container',
  templateUrl: './messages-container.component.html',
  styleUrls: ['./messages-container.component.css']
})
@RegisterClass(BaseResourceComponent, 'IzzyMessagesContainerResource')
export class IzzyMessagesContainerResource extends BaseResourceComponent implements OnInit, OnDestroy {

  async GetResourceDisplayName(data: ResourceData): Promise<string> {
    return 'Messages';
  }

  async GetResourceIconClass(data: ResourceData): Promise<string> {
    return 'fa-solid fa-envelope';
  }

  constructor(
    private route: ActivatedRoute,
    private location: Location,
    private authService: IzzyAuthService,
    private contextService: IzzyContextService
  ) {
    super();
    this.selectedSection = this.sections[0];
  }

  // ========== Section Configuration ==========
  protected sections: MessagesSection[] = [
    {
      id: 'review',
      label: 'Review & Approve',
      icon: 'fa-check-circle',
      description: 'Review pending messages and approve or reject responses'
    }
  ];

  protected selectedSection: MessagesSection;

  // ========== Organization/Channel Context ==========
  protected organizations: mjBizAppsCommonOrganizationEntity[] = [];
  protected channels: BCSaaSChannelEntity[] = [];
  protected selectedOrg: mjBizAppsCommonOrganizationEntity | null = null;
  protected selectedOrganizationId: string | null = null;
  protected selectedChannel: BCSaaSChannelEntity | null = null;
  protected hasMultipleOrgs: boolean = false;
  private channelOrder: Record<string, string[]> = {}; // orgId -> ordered channelIds

  // ========== UI State ==========
  protected isInitializing: boolean = true; // True until user settings and initial data are loaded
  protected isNavCollapsed: boolean = false;
  protected isLoading: boolean = true;
  protected isSystemDeveloper: boolean = false;

  // ========== User Settings Persistence ==========
  private userSettingsKey = 'Izzy.Messages';
  private userSetting: MJUserSettingEntity | null = null;

  // ========== ViewChild References ==========
  @ViewChild('orgSelector') orgSelector?: OrgSelectorComponent;

  async ngOnInit(): Promise<void> {
    // Check if user is system developer
    this.isSystemDeveloper = await this.authService.isSystemDeveloper();

    // Load data first
    await this.loadOrganizations();

    // Now apply selections with priority: (1) URL params, (2) User settings, (3) First org
    await this.applyInitialSelections();

    // Publish what this section actually resolved (org AND channel) to the shared context so
    // the header switcher shows the same thing the page loaded. Priority above already ranks
    // URL > context > settings, so the section's answer is authoritative — writing it back is
    // safe, and the service's no-op setters prevent echo loops. The previous "only if empty"
    // guard let a URL/settings-driven org differ from a switcher-seeded one indefinitely.
    if (this.selectedOrg) {
      this.contextService.selectedOrganizationId = this.selectedOrg.ID;
    }
    if (this.selectedChannel) {
      this.contextService.selectedChannelId = this.selectedChannel.ID;
    }

    // Subscribe to query param changes for deep linking (after initial load)
    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe(params => {
      this.handleQueryParamsChange(params);
    });

    // React to org changes made in other Izzy sections while this tab stays alive.
    // skip(1) ignores the BehaviorSubject replay — the initial selection above
    // already consulted the context (with URL params taking precedence).
    this.contextService.orgChanges$
      .pipe(skip(1), takeUntil(this.destroy$))
      .subscribe(orgId => this.onContextOrgChanged(orgId));

    // Channel changes come from the global header switcher now (the page's
    // own channel dropdown was removed in its favor).
    this.contextService.channelChanges$
      .pipe(skip(1), takeUntil(this.destroy$))
      .subscribe(channelId => this.onContextChannelChanged(channelId));

    // Initialization complete - safe to render
    this.isInitializing = false;

    this.NotifyLoadComplete();
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  // ========== User Settings Persistence ==========

  /**
   * Apply initial selections with priority:
   * 1. URL params (highest)
   * 2. User Settings
   * 3. First org/channel (lowest)
   */
  private async applyInitialSelections(): Promise<void> {
    // Get URL params
    const urlParams = this.route.snapshot.queryParams;
    const urlOrgId = urlParams['orgId'];
    const urlChannelId = urlParams['channelId'];
    const urlSection = urlParams['section'];

    // Load user settings
    let userSettings: MessagesContainerSettings | null = null;
    try {
      const md = new Metadata();
      const currentUserId = md.CurrentUser.ID;

      const rv = new RunView();
      const result = await rv.RunView<MJUserSettingEntity>({
        EntityName: 'MJ: User Settings',
        ExtraFilter: `Setting = '${this.userSettingsKey}' AND UserID = '${currentUserId}'`,
        ResultType: 'entity_object'
      });

      if (result?.Success && result.Results.length > 0) {
        this.userSetting = result.Results[0];
        userSettings = JSON.parse(this.userSetting.Value || '{}') as MessagesContainerSettings;

        // Always apply nav collapsed state from settings
        if (userSettings.isNavCollapsed !== undefined) {
          this.isNavCollapsed = userSettings.isNavCollapsed;
        }
        // Restore channel ordering preference
        if (userSettings.channelOrder) {
          this.channelOrder = userSettings.channelOrder;
        }
      }
    } catch (error) {
      console.error('Messages Container: Error loading user settings:', error);
    }

    // Determine org ID: URL > Context Service > UserSettings > First
    let orgId: string | undefined = urlOrgId;
    if (!orgId && this.contextService.selectedOrganizationId) {
      orgId = this.contextService.selectedOrganizationId;
    }
    if (!orgId && userSettings?.organizationId) {
      orgId = userSettings.organizationId;
    }

    // Apply org selection
    if (orgId) {
      const org = this.organizations.find(o => o.ID === orgId);
      if (org) {
        this.selectedOrg = org;
        this.selectedOrganizationId = org.ID;
      }
    }
    // Fallback to first org
    if (!this.selectedOrg && this.organizations.length > 0) {
      this.selectedOrg = this.organizations[0];
      this.selectedOrganizationId = this.selectedOrg.ID;
    }

    // Determine channel ID: URL > Context Service > UserSettings > First for selected org.
    // Context was missing here (org already consulted it) — so the header switcher's channel
    // and this section's channel could resolve differently and never reconcile.
    let channelId: string | undefined = urlChannelId;
    if (!channelId && this.contextService.selectedChannelId) {
      channelId = this.contextService.selectedChannelId;
    }
    if (!channelId && userSettings?.channelId) {
      channelId = userSettings.channelId;
    }

    // Apply channel selection (must belong to selected org)
    if (channelId && this.selectedOrg) {
      const orgChannels = this.getOrgChannels(this.selectedOrg);
      const channel = orgChannels.find(c => c.ID === channelId);
      if (channel) {
        this.selectedChannel = channel;
      }
    }
    // Fallback to first channel for selected org
    if (!this.selectedChannel && this.selectedOrg) {
      const orgChannels = this.getOrgChannels(this.selectedOrg);
      if (orgChannels.length > 0) {
        this.selectedChannel = orgChannels[0];
      }
    }

    // Determine section: URL > UserSettings > First
    let sectionId: string | undefined = urlSection;
    if (!sectionId && userSettings?.selectedSection) {
      sectionId = userSettings.selectedSection;
    }
    if (sectionId) {
      const section = this.sections.find(s => s.id === sectionId);
      if (section) {
        this.selectedSection = section;
      }
    }

    // Update URL to reflect current state
    this.updateUrl();
  }

  private async saveUserSettings(): Promise<void> {
    try {
      const md = new Metadata();
      const settings: MessagesContainerSettings = {
        isNavCollapsed: this.isNavCollapsed,
        organizationId: this.selectedOrg?.ID,
        channelId: this.selectedChannel?.ID,
        selectedSection: this.selectedSection.id,
        channelOrder: this.channelOrder
      };

      if (!this.userSetting) {
        this.userSetting = await md.GetEntityObject<MJUserSettingEntity>('MJ: User Settings');
        this.userSetting.Setting = this.userSettingsKey;
        this.userSetting.UserID = md.CurrentUser.ID;
      }

      this.userSetting.Value = JSON.stringify(settings);
      await this.userSetting.Save();
    } catch (error) {
      console.error('Messages Container: Error saving user settings:', error);
    }
  }

  // ========== Data Loading ==========

  private async loadOrganizations(): Promise<void> {
    this.isLoading = true;
    try {
      await IzzyMetadataEngine.Instance.Config(false);
      this.organizations = IzzyMetadataEngine.Instance.GetCurrentUserOrganizations();
      this.channels = IzzyMetadataEngine.Instance.Channels;
      this.hasMultipleOrgs = this.organizations.length > 1;

    } catch (error) {
      console.error('Messages Container: Error loading organizations:', error);
    } finally {
      this.isLoading = false;
    }
  }

  // ========== Query Param Changes (for deep linking) ==========

  /**
   * Handle query param changes after initial load
   */
  private handleQueryParamsChange(params: { [key: string]: string }): void {
    // Apply changes directly since data is already loaded
    if (params['section']) {
      const section = this.sections.find(s => s.id === params['section']);
      if (section && section !== this.selectedSection) {
        this.selectedSection = section;
      }
    }
    if (params['orgId']) {
      const org = this.organizations.find(o => o.ID === params['orgId']);
      if (org && org !== this.selectedOrg) {
        this.selectOrg(org);
      }
    }
    if (params['channelId']) {
      const channel = this.channels.find(c => c.ID === params['channelId']);
      if (channel && channel !== this.selectedChannel) {
        this.selectChannel(channel);
      }
    }
  }

  // ========== Section Selection ==========

  selectSection(section: MessagesSection): void {
    this.selectedSection = section;
    this.saveUserSettings();
    this.updateUrl();
  }

  // ========== Organization/Channel Selection ==========

  /**
   * Handler for the shared OrgSelectorComponent.
   * Receives OrganizationEntity | null (null not used here since allowAll=false)
   *
   * `syncUrl` is false only for context-originated changes (another section's
   * org switch while this tab is hidden) — location.replaceState is global, so
   * a hidden tab must not rewrite the visible tab's URL.
   */
  onOrgSelectorChange(org: mjBizAppsCommonOrganizationEntity | null, syncUrl: boolean = true): void {
    this.selectedOrg = org;
    this.selectedOrganizationId = org?.ID || null;
    this.contextService.selectedOrganizationId = org?.ID || null;

    // Auto-select first channel for new org
    if (org) {
      const orgChannels = this.getOrgChannels(org);
      if (orgChannels.length > 0) {
        this.selectedChannel = orgChannels[0];
        this.contextService.selectedChannelId = orgChannels[0].ID;
      } else {
        this.selectedChannel = null;
        this.contextService.selectedChannelId = null;
      }
    } else {
      this.selectedChannel = null;
      this.contextService.selectedChannelId = null;
    }

    this.saveUserSettings();
    if (syncUrl) {
      this.updateUrl();
    }
  }

  /**
   * Select an organization (called by query param changes)
   */
  selectOrg(org: mjBizAppsCommonOrganizationEntity): void {
    this.onOrgSelectorChange(org);
  }

  /**
   * Apply an org change that originated in another Izzy section.
   * A null context (e.g. "All Organizations" in Analytics) is ignored
   * because Messages always requires a concrete org.
   */
  private onContextOrgChanged(orgId: string | null): void {
    if (!orgId || UUIDsEqual(orgId, this.selectedOrg?.ID)) {
      return;
    }
    const org = this.organizations.find(o => UUIDsEqual(o.ID, orgId));
    if (org) {
      this.onOrgSelectorChange(org, false);
    }
  }

  private onContextChannelChanged(channelId: string | null): void {
    if (!channelId || UUIDsEqual(channelId, this.selectedChannel?.ID)) {
      return;
    }
    const channel = this.selectedOrg
      ? this.getOrgChannels(this.selectedOrg).find(c => UUIDsEqual(c.ID, channelId))
      : null;
    if (channel) {
      this.selectChannel(channel);
    }
  }

  /**
   * Select a channel (called by user interaction)
   */
  selectChannel(channel: BCSaaSChannelEntity): void {
    this.selectedChannel = channel;
    this.contextService.selectedChannelId = channel.ID;
    this.saveUserSettings();
    this.updateUrl();
  }

  getOrgChannels(org: mjBizAppsCommonOrganizationEntity | null): BCSaaSChannelEntity[] {
    if (!org) return [];
    const orgChannels = this.channels.filter(c => c.OrganizationID === org.ID);
    const order = this.channelOrder[org.ID];
    if (!order || order.length === 0) {
      return orgChannels.sort((a, b) => a.Name.localeCompare(b.Name));
    }
    return orgChannels.sort((a, b) => {
      const aIdx = order.indexOf(a.ID);
      const bIdx = order.indexOf(b.ID);
      // Channels not in the order go to the end, sorted by name
      if (aIdx === -1 && bIdx === -1) return a.Name.localeCompare(b.Name);
      if (aIdx === -1) return 1;
      if (bIdx === -1) return -1;
      return aIdx - bIdx;
    });
  }

  private draggedChannelIndex: number = -1;

  onChannelDragStart(index: number): void {
    this.draggedChannelIndex = index;
  }

  onChannelDragOver(event: DragEvent, index: number): void {
    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move';
    }
  }

  onChannelDrop(event: DragEvent, dropIndex: number): void {
    event.preventDefault();
    if (!this.selectedOrg || this.draggedChannelIndex === -1 || this.draggedChannelIndex === dropIndex) {
      this.draggedChannelIndex = -1;
      return;
    }
    const channels = this.getOrgChannels(this.selectedOrg);
    const [moved] = channels.splice(this.draggedChannelIndex, 1);
    channels.splice(dropIndex, 0, moved);
    this.channelOrder[this.selectedOrg.ID] = channels.map(c => c.ID);
    this.draggedChannelIndex = -1;
    this.saveUserSettings();
  }

  onChannelDragEnd(): void {
    this.draggedChannelIndex = -1;
  }

  getOrgInitials(name: string): string {
    if (!name) return '?';
    const words = name.split(' ').filter(w => w.length > 0);
    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  }

  // ========== Nav State ==========

  toggleNav(): void {
    this.isNavCollapsed = !this.isNavCollapsed;
    this.saveUserSettings();
  }

  /**
   * Expands the nav and opens the org selector dropdown.
   * Called when clicking the collapsed org indicator.
   */
  expandNavAndOpenOrgSelector(): void {
    this.isNavCollapsed = false;
    this.saveUserSettings();
    // Use setTimeout to wait for the nav to expand and org selector to render
    setTimeout(() => {
      this.orgSelector?.openDropdown();
    }, 100);
  }

  // ========== URL State Management ==========

  private updateUrl(): void {
    const params = new URLSearchParams();
    if (this.selectedSection) {
      params.set('section', this.selectedSection.id);
    }
    if (this.selectedOrg) {
      params.set('orgId', this.selectedOrg.ID);
    }
    if (this.selectedChannel) {
      params.set('channelId', this.selectedChannel.ID);
    }

    const path = this.location.path().split('?')[0];
    const queryString = params.toString();
    this.location.replaceState(path, queryString);
  }
}

/**
 * Utility function to prevent tree-shaking
 */
export function LoadMessagesContainerComponent(): void {
  // Intentionally empty
}
